import type { FormData } from '../types';

interface Props {
  data: FormData;
  onEditStep?: (step: number) => void;
  /** MUL-3919: when the sub isn't eligible, the wizard hides "Agreement Terms". */
  paymentTermsNegotiable: boolean;
}

function SectionHeader({ title, stepIndex, onEdit }: { title: string; stepIndex: number; onEdit?: (step: number) => void }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: 8, borderBottom: '1px solid #E5E7EB', marginBottom: 12 }}>
      <h3 style={{ color: '#0D2152', fontSize: 16, fontWeight: 600, margin: 0 }}>{title}</h3>
      {onEdit && (
        <button
          type="button"
          onClick={() => onEdit(stepIndex)}
          className="link-btn"
          style={{ fontSize: 13 }}
        >
          Edit
        </button>
      )}
    </div>
  );
}

// MUL-2333: the HTS default is paid-when-paid (null). Net 30/45/60 are
// situational overrides that route to the approval queue. The Review step
// tells the sub which lane their submission is in before they hit Submit.
function describePaymentTerms(value: number | null | undefined): {
  label: React.ReactNode;
  isStandard: boolean;
} {
  if (value === null || value === undefined) {
    return { label: 'Paid when paid — HTS default', isStandard: true };
  }
  return {
    label: <>Standard: Paid when paid &rarr; Requested: Net {value}</>,
    isStandard: false,
  };
}

export function ReviewStep({ data, onEditStep, paymentTermsNegotiable }: Props) {
  const requestedDays = data.requestedTerms?.paymentTermsDays;
  const { label: paymentTermsLabel, isStandard: paymentTermsStandard } = describePaymentTerms(requestedDays);
  const requestReason = (data.requestReason ?? '').trim();
  return (
    <div>
      <div className="step-header">
        <h2>Review Your Information</h2>
        <p>Please look over everything below to make sure it is correct. Click "Edit" next to any section to go back and make changes.</p>
      </div>

      <div className="review-section">
        <SectionHeader title="Company Information" stepIndex={0} onEdit={onEditStep} />
        <div className="review-grid">
          <span className="review-label">Company Name</span>
          <span className="review-value">{data.companyName}</span>
          <span className="review-label">Address</span>
          <span className="review-value">
            {data.addressLine1}
            {data.addressLine2 && <>, {data.addressLine2}</>}
            <br />
            {data.city}, {data.state} {data.zip}
          </span>
          <span className="review-label">Phone</span>
          <span className="review-value">{data.phone}</span>
          {data.website && (
            <>
              <span className="review-label">Website</span>
              <span className="review-value">{data.website}</span>
            </>
          )}
          <span className="review-label">EIN / Tax ID</span>
          <span className="review-value">{data.einTaxId}</span>
        </div>
      </div>

      <div className="review-section">
        <SectionHeader title="Primary Contact" stepIndex={1} onEdit={onEditStep} />
        <div className="review-grid">
          <span className="review-label">Name</span>
          <span className="review-value">
            {data.contactFirstName} {data.contactLastName}
          </span>
          <span className="review-label">Email</span>
          <span className="review-value">{data.contactEmail}</span>
          <span className="review-label">Phone</span>
          <span className="review-value">{data.contactPhone}</span>
          {data.contactTitle && (
            <>
              <span className="review-label">Title</span>
              <span className="review-value">{data.contactTitle}</span>
            </>
          )}
        </div>
      </div>

      {data.hasAlternativeSigner && (
        <div className="review-section">
          <SectionHeader title="Authorized Signer" stepIndex={1} onEdit={onEditStep} />
          <div className="review-grid">
            <span className="review-label">Name</span>
            <span className="review-value">
              {data.signerFirstName} {data.signerLastName}
            </span>
            <span className="review-label">Email</span>
            <span className="review-value">{data.signerEmail}</span>
            {data.signerTitle && (
              <>
                <span className="review-label">Title</span>
                <span className="review-value">{data.signerTitle}</span>
              </>
            )}
          </div>
        </div>
      )}

      <div className="review-section">
        <SectionHeader title="Skills & Coverage" stepIndex={2} onEdit={onEditStep} />
        <div className="review-grid">
          <span className="review-label">Services</span>
          <div className="review-chips">
            {data.skills.map((s) => (
              <span key={s} className="review-chip">{s}</span>
            ))}
          </div>
          <span className="review-label">Travel Distance</span>
          <span className="review-value">{data.serviceRadiusMi} miles</span>
          {data.isNationalBroker && (
            <>
              <span className="review-label">Nationwide</span>
              <span className="review-value">Yes</span>
            </>
          )}
        </div>
      </div>

      {data.licenses.length > 0 && (
        <div className="review-section">
          <SectionHeader title="Licenses" stepIndex={3} onEdit={onEditStep} />
          {data.licenses.map((lic, i) => (
            <div key={i} className="review-grid" style={{ marginBottom: 8 }}>
              <span className="review-label">License #{i + 1}</span>
              <span className="review-value">
                {lic.type} — {lic.number} ({lic.state}, expires {lic.expiresAt})
              </span>
            </div>
          ))}
        </div>
      )}

      {paymentTermsNegotiable && (
        <div className="review-section">
          <SectionHeader title="Agreement Terms" stepIndex={5} onEdit={onEditStep} />
        <div className="review-grid">
          <span className="review-label">Payment terms</span>
          <span className="review-value">{paymentTermsLabel}</span>
        </div>
        {!paymentTermsStandard && (
          <>
            {requestReason && (
              <div className="review-grid" style={{ marginTop: 12 }}>
                <span className="review-label">Request reason</span>
                <span className="review-value" style={{ whiteSpace: 'pre-wrap' }}>{requestReason}</span>
              </div>
            )}
            <p className="field-hint" style={{ marginTop: 12 }}>
              Your application will pause for HTS review before any agreement is sent.
            </p>
          </>
        )}
      </div>
      )}

      <div className="review-section">
        <SectionHeader title="Documents Uploaded" stepIndex={4} onEdit={onEditStep} />
        <div className="review-grid">
          <span className="review-label">Insurance (COI)</span>
          <span className="review-value">
            {data.coiFile ? (
              <span style={{ color: 'var(--hts-success)', fontWeight: 500 }}>&#10003; {data.coiFile.name}</span>
            ) : (
              <span style={{ color: 'var(--hts-error)' }}>Not uploaded</span>
            )}
          </span>
          <span className="review-label">W-9 Form</span>
          <span className="review-value">
            {data.w9File ? (
              <span style={{ color: 'var(--hts-success)', fontWeight: 500 }}>&#10003; {data.w9File.name}</span>
            ) : (
              <span style={{ color: 'var(--hts-error)' }}>Not uploaded</span>
            )}
          </span>
          <span className="review-label">Bank Verification</span>
          <span className="review-value">
            {data.bankVerifyFile ? (
              <span style={{ color: 'var(--hts-success)', fontWeight: 500 }}>&#10003; {data.bankVerifyFile.name}</span>
            ) : (
              <span style={{ color: 'var(--hts-error)' }}>Not uploaded</span>
            )}
          </span>
        </div>
      </div>

      <div className="review-section">
        <SectionHeader title="Payment Information" stepIndex={7} onEdit={onEditStep} />
        {data.paymentMethod === 'check' ? (
          <p className="review-value">Paper check — mailed to your business address</p>
        ) : (
          <div className="review-grid">
            <span className="review-label">Vendor Name</span>
            <span className="review-value">{data.achVendorName}</span>
            <span className="review-label">Bank</span>
            <span className="review-value">{data.achBankName}</span>
            <span className="review-label">Routing Number</span>
            <span className="review-value">{data.achRoutingNumber}</span>
            <span className="review-label">Account Number</span>
            <span className="review-value">****{data.achAccountNumber.slice(-4)}</span>
            <span className="review-label">Account Type</span>
            <span className="review-value" style={{ textTransform: 'capitalize' }}>{data.achAccountType}</span>
          </div>
        )}
      </div>
    </div>
  );
}
